/**
 * Media Preview Card Component
 * Unified component for displaying both videos and images with controls
 * Supports auto-polling for video status updates
 */

import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Image, Video } from "@/features/studio-ai/types/studio-ai.types"

import {
  Download,
  Eye,
  Pencil,
  Play,
  RefreshCw,
  Shuffle,
  Sparkles,
  Trash2,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useGenerationMonitor } from "@/features/studio-ai/hooks/use-generation-monitor"
import { useImageThumbnailLazyRequest } from "@/features/studio-ai/hooks/use-image-thumbnail-lazy-request"
import { useStudioImageThumbnail } from "@/features/studio-ai/hooks/use-studio-image-thumbnail"
import { MediaStatus, MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { isActiveMediaStatus } from "@/features/studio-ai/utils/media-status-utils"
import { ImageCacheService } from "@/services/image-cache-service"
import { useStudioAIStore } from "@/stores/studio-ai-store"

import { MediaStatusBadge } from "./video-status-badge"

type MediaPreviewCardProps =
  | {
      mediaType: MediaType.Video
      media: Video
      editCount?: number
      onPlay: () => void
      onDownload?: () => void
      onDelete?: () => void
    }
  | {
      mediaType: MediaType.Image
      media: Image
      editCount?: number
      onView: () => void
      onDownload?: () => void
      onDelete?: () => void
    }

// Constants for notification display
const PROMPT_PREVIEW_LENGTH = 50
const ERROR_TOAST_DURATION_MS = 10000 // 10 seconds

export function MediaPreviewCard(props: MediaPreviewCardProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.mediaCard" })
  const { t: tCommon } = useTranslation(undefined, { keyPrefix: "studioAi.common" })
  const { mediaType, media, onDownload, onDelete, editCount } = props

  const isVideo = mediaType === MediaType.Video
  const isCompleted = media.status === MediaStatus.Completed
  // Every non-terminal status, `running` included: the backend only learns a
  // video finished when it is asked, so the monitor must keep asking.
  const isProcessing = isActiveMediaStatus(media.status)

  const setRemixVideoId = useStudioAIStore((state) => state.setRemixVideoId)
  const setCurrentView = useStudioAIStore((state) => state.setCurrentView)
  const setEditorImageUrl = useStudioAIStore((state) => state.setEditorImageUrl)
  const setEditorImageJobId = useStudioAIStore((state) => state.setEditorImageJobId)
  const setEditorSourceJobId = useStudioAIStore((state) => state.setEditorSourceJobId)

  // Auto-monitor for status updates (videos only)
  const { refetch } = useGenerationMonitor({
    jobId: isVideo ? media.job_id : null,
    enabled: isVideo && isProcessing && !!media.job_id,
    mediaType: "video",
    onComplete: (result) => {
      // Notify user of successful completion
      const promptPreview =
        result.prompt && result.prompt.trim()
          ? `"${result.prompt.substring(0, PROMPT_PREVIEW_LENGTH)}${result.prompt.length > PROMPT_PREVIEW_LENGTH ? "..." : ""}"`
          : t("yourVideoIsReady")
      toast.success(t("videoCompleted"), {
        description: promptPreview,
      })
    },
    onError: (error) => {
      // Notify user of failure with error details
      toast.error(t("videoFailed"), {
        description: error,
        duration: ERROR_TOAST_DURATION_MS,
      })
    },
  })

  const handleRefreshStatus = () => {
    if (isVideo && media.job_id) {
      void refetch()
    }
  }

  const handleRemixVideo = () => {
    if (!isVideo) return
    const videoId = media.job_id || media.id
    setRemixVideoId(videoId)
    toast.success(t("videoRemixSelected"), {
      description: t("videoRemixDesc", { id: videoId }),
    })
  }

  const handleEditImage = () => {
    if (isVideo) return
    const imageUrl = media.image_url
    if (imageUrl) {
      setEditorImageUrl(imageUrl)
      setEditorImageJobId(media.job_id)
      setEditorSourceJobId((media as Image).source_job_id || media.job_id)
      setCurrentView("image-editor")
    }
  }

  const handleMainAction = () => {
    if (isVideo) {
      const { onPlay } = props
      onPlay()
    } else {
      const { onView } = props
      onView()
    }
  }

  // Get media URL (fall back to ImageCacheService for images without stored URL)
  const cachedLegacyUrl = useMemo(
    () => (!isVideo ? ImageCacheService.getImage(media.job_id) : null),
    [isVideo, media.job_id]
  )
  const legacyImageUrl = !isVideo ? media.image_url || cachedLegacyUrl : null
  const mediaUrl = isVideo ? media.video_url : legacyImageUrl
  // Resolve the image src through the batch-prefetch cache so the <img>
  // tag gets a blob: URL it can render without auth headers. Videos still
  // use their direct thumbnail_url (Sora exposes a SAS link).
  //
  // Only fire the resolver for COMPLETED images — processing jobs don't
  // have a blob yet, so the batch endpoint reports them in `missing` and
  // the cache marks them as failed. Once an id is failed, the prefetch
  // hook's seen-set short-circuits future fetches, so even after the job
  // completes the thumbnail never loads. The cards for non-completed jobs
  // render the spinner/status placeholder anyway, so passing undefined
  // here just keeps the resolver out of the way until the job is ready.
  const thumbnailJobId = !isVideo && isCompleted ? media.job_id : undefined
  const imageThumbnail = useStudioImageThumbnail(thumbnailJobId, legacyImageUrl, {
    autoRequest: false,
  })
  const thumbnailUrl = isVideo ? (media as Video).thumbnail_url : imageThumbnail.url
  // Attach the IntersectionObserver fallback so off-screen cards that
  // mount but never enter the viewport don't burn bandwidth (relevant
  // when the user filters/searches and the result set is small but the
  // underlying job list is large). Same completion gate as the resolver.
  const ioRef = useImageThumbnailLazyRequest(thumbnailJobId)

  // Choose the preview content per state. Pulled into a function to avoid
  // a nested ternary (eslint no-nested-ternary).
  //  1. URL ready             → button-wrapped <img>
  //  2. Image, fetch pending  → spinner (batch in flight)
  //  3. Video, no thumbnail   → Play icon placeholder
  //  4. Image, failed/missing → 'image not available' placeholder
  const renderPreview = () => {
    if (thumbnailUrl) {
      return (
        <button
          className="h-full w-full cursor-pointer object-cover transition-opacity group-hover:opacity-90"
          type="button"
          onClick={handleMainAction}
        >
          <img
            alt={media.prompt}
            className="h-full w-full object-cover"
            src={thumbnailUrl}
            onError={imageThumbnail.onImgError}
          />
        </button>
      )
    }
    if (!isVideo && imageThumbnail.loading) {
      // Batch fetch in flight — show a spinner instead of jumping to the
      // unavailable placeholder. Without this the card flashes "image not
      // available" for the half-second between mount and the blob URL arriving.
      return (
        <div className="bg-muted flex h-full w-full items-center justify-center">
          <Spinner className="text-muted-foreground h-8 w-8" />
        </div>
      )
    }
    return (
      <div className="bg-muted flex h-full w-full items-center justify-center">
        {isVideo ? (
          <Play className="text-muted-foreground h-12 w-12 opacity-50" />
        ) : (
          <div className="text-center">
            <Eye className="text-muted-foreground mx-auto mb-2 h-12 w-12 opacity-50" />
            <p className="text-muted-foreground text-xs">{t("imageNotAvailable")}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <Card ref={ioRef} className="group overflow-hidden transition-shadow hover:shadow-lg">
      {/* Media Preview */}
      <div className={`bg-muted relative ${isVideo ? "aspect-video" : "aspect-square"}`}>
        {isCompleted && mediaUrl ? (
          <div className="relative h-full w-full">
            {renderPreview()}

            {/* Hover Overlay */}
            <div className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] group-hover:bg-black/30 group-hover:opacity-100">
              <div className="flex items-center gap-2 px-4 sm:gap-3 md:gap-4">
                {mediaUrl && (
                  <>
                    {/* Download Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          aria-label={isVideo ? t("downloadVideo") : t("downloadImage")}
                          className="bg-background/90 focus-visible:ring-ring flex h-10 w-10 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDownload?.()
                          }}
                        >
                          <Download className="text-muted-foreground h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isVideo ? t("downloadVideo") : t("downloadImage")}
                      </TooltipContent>
                    </Tooltip>

                    {/* Remix Button (videos only) */}
                    {isVideo && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={t("remixThisVideo")}
                            className="bg-background/90 focus-visible:ring-ring flex h-10 w-10 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRemixVideo()
                            }}
                          >
                            <Shuffle className="text-foreground h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t("remixThisVideo")}</TooltipContent>
                      </Tooltip>
                    )}

                    {/* Edit Button (images only) */}
                    {!isVideo && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={t("editImage")}
                            className="bg-background/90 focus-visible:ring-ring flex h-10 w-10 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditImage()
                            }}
                          >
                            <Pencil className="text-primary h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t("editImage")}</TooltipContent>
                      </Tooltip>
                    )}

                    {/* Main Action Button (Play/View - Center, Larger) */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          aria-label={isVideo ? t("playVideo") : t("viewImage")}
                          className={`bg-background/90 focus-visible:ring-ring flex items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                            isVideo ? "h-16 w-16" : "h-14 w-14"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMainAction()
                          }}
                        >
                          {isVideo ? (
                            <Play className="text-primary ml-1 h-8 w-8" />
                          ) : (
                            <Eye className="text-primary h-7 w-7" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{isVideo ? t("playVideo") : t("viewImage")}</TooltipContent>
                    </Tooltip>
                  </>
                )}

                {/* Delete Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label={isVideo ? t("deleteVideo") : t("deleteImage")}
                      className="bg-background/90 focus-visible:ring-ring flex h-10 w-10 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete?.()
                      }}
                    >
                      <Trash2 className="text-destructive h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{isVideo ? t("deleteVideo") : t("deleteImage")}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            {isProcessing ? (
              <div className="text-center">
                <Spinner className="text-primary mx-auto mb-2 size-12" />
                <p className="text-muted-foreground text-sm">
                  {isVideo ? tCommon("processing") : tCommon("generating")}
                </p>
              </div>
            ) : (
              <div className="text-muted-foreground text-center">
                {isVideo ? (
                  <Play className="mx-auto mb-2 h-12 w-12 opacity-50" />
                ) : (
                  <Eye className="mx-auto mb-2 h-12 w-12 opacity-50" />
                )}
                <p className="text-sm">{media.status}</p>
                {media.error_message && (
                  <p className="text-destructive mt-2 px-4 text-xs" role="alert">
                    {media.error_message}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status Badge Overlay */}
        {media.status !== MediaStatus.Completed && (
          <div className="absolute top-2 right-2">
            <MediaStatusBadge status={media.status} />
          </div>
        )}

        {/* Edit Count Badge */}
        {editCount != null && editCount > 0 && (
          <div className="absolute top-2 left-2">
            <Badge className="border-foreground bg-primary text-primary-foreground text-xs shadow-md">
              {editCount} {editCount === 1 ? "edit" : "edits"}
            </Badge>
          </div>
        )}
      </div>

      {/* Card Content */}
      <CardContent className="p-4">
        {/* Prompt */}
        <p className="mb-3 line-clamp-2 text-sm" title={media.prompt}>
          {media.prompt}
        </p>

        {/* Meta Information */}
        <div className="mb-3 flex flex-wrap gap-2">
          {isVideo ? (
            <>
              <Badge className="text-xs" variant="outline">
                {(media as Video).duration}s
              </Badge>
              <Badge className="text-xs" variant="outline">
                {(media as Video).aspect_ratio}
              </Badge>
              <Badge className="text-xs" variant="outline">
                {(media as Video).style}
              </Badge>
              {(media as Video).remix_from_video_id && (
                <Badge className="text-xs" variant="secondary">
                  <Shuffle className="mr-1 h-3 w-3" />
                  {tCommon("remix")}
                </Badge>
              )}
            </>
          ) : (
            <>
              <Badge className="text-xs" variant="outline">
                {(media as Image).size}
              </Badge>
              <Badge className="text-xs" variant="outline">
                {(media as Image).quality}
              </Badge>
              {(media as Image).edit_mode && (
                <Badge className="text-xs" variant="secondary">
                  {tCommon("edited")}
                </Badge>
              )}
              {(media as Image).reference_image_url && (
                <Badge className="text-xs" variant="secondary">
                  <Sparkles className="mr-1 h-3 w-3" />
                  {tCommon("reference")}
                </Badge>
              )}
            </>
          )}
        </div>

        {/* Error Message */}
        {media.error_message && (
          <p className="text-destructive mb-3 text-xs" role="alert" title={media.error_message}>
            {media.error_message}
          </p>
        )}

        {/* Actions */}
        {!isCompleted && isVideo && (
          <div className="flex gap-2">
            <Button
              size="sm"
              title={tCommon("refreshStatus")}
              variant="outline"
              onClick={handleRefreshStatus}
            >
              <RefreshCw className="h-3 w-3" />
              {tCommon("refreshStatus")}
            </Button>
          </div>
        )}

        {/* Timestamp */}
        {media.created_at && (
          <p className="text-muted-foreground mt-2 text-xs">
            {tCommon("created")}: {new Date(media.created_at).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
